(() => {
  // Product/cart pages keep their existing direct SDK tag. This loader is only
  // for other pages: Shopify's PayPal payment buttons are never touched here.
  if (window.shopdePayPalMessages) return;
  const config = document.currentScript;
  const sdkURL = config?.dataset.sdkUrl;
  const namespace = config?.dataset.namespace;
  if (!sdkURL || !namespace) return;

  const selector = '[data-pp-message]';
  const drawers = new Map();
  const handled = new WeakSet();
  let loading;
  let lifecycle;

  const api = () => typeof window[namespace]?.Messages === 'function' ? window[namespace] : null;
  const open = (drawer) => !!lifecycle && drawer.isConnected && drawer.classList.contains('active');
  const messages = (root) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector),
  ];

  function loadSDK() {
    if (api()) return Promise.resolve(api());
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = sdkURL;
      script.async = true;
      script.dataset.namespace = namespace;
      script.dataset.shopdePaypalSdk = '';
      const finish = (success) => {
        script.onload = script.onerror = null;
        const sdk = api();
        if (success && sdk) {
          // The first SDK load handles existing native data-pp-message targets.
          // Reserve those targets so we do not render a second time ourselves.
          drawers.forEach((_observers, drawer) => messages(drawer).forEach((target) => handled.add(target)));
          resolve(sdk);
        } else {
          // Only a native error proves this request has ended. A slow script
          // stays in flight; removing it on a timer could still let it execute.
          if (!success) {
            script.remove();
            loading = null;
          }
          reject(new Error('Pay Later messaging is unavailable'));
        }
      };
      script.onload = () => finish(true);
      script.onerror = () => finish(false);
      document.head.append(script);
    });
    return loading;
  }

  function renderNew(drawer, targets) {
    const sdk = api();
    if (!sdk || !open(drawer)) return;
    targets.forEach((target) => {
      if (!target.isConnected || !drawer.contains(target) || handled.has(target)) return;
      // A previously present namespace may already have rendered this element.
      if (target.querySelector('iframe')) { handled.add(target); return; }
      handled.add(target);
      try {
        // Existing native amount/style attributes remain the source of truth.
        // PayPal itself observes later data-pp-* amount changes.
        Promise.resolve(sdk.Messages({}).render(target)).catch(() => handled.delete(target));
      } catch {
        handled.delete(target);
      }
    });
  }

  function initialize(drawer) {
    if (!open(drawer)) return;
    loadSDK().then(() => {
      if (open(drawer)) renderNew(drawer, messages(drawer));
    }).catch(() => {
      // Optional messages never block cart forms or checkout. A native load
      // error permits retry on a later drawer opening/cart replacement.
    });
  }

  function bind(drawer) {
    if (drawers.has(drawer)) return;
    const state = new MutationObserver(() => initialize(drawer));
    state.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    const content = new MutationObserver((records) => {
      if (!open(drawer)) return;
      const added = [];
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node instanceof Element) added.push(...messages(node));
      }));
      if (!added.length) return; // Ignore SDK-internal iframe/status mutations.
      if (api()) renderNew(drawer, added);
      else initialize(drawer);
    });
    content.observe(drawer, { childList: true, subtree: true });
    drawers.set(drawer, { state, content });
    initialize(drawer);
  }

  function refresh(root = document) {
    if (!lifecycle) return;
    drawers.forEach((observers, drawer) => {
      if (!drawer.isConnected) {
        observers.state.disconnect();
        observers.content.disconnect();
        drawers.delete(drawer);
      }
    });
    if (root.matches?.('cart-drawer')) bind(root);
    root.querySelectorAll('cart-drawer').forEach(bind);
  }

  function start() {
    if (lifecycle) return;
    lifecycle = new AbortController();
    document.addEventListener('shopify:section:load', (event) => refresh(event.target), { signal: lifecycle.signal });
    document.addEventListener('shopify:section:unload', (event) => {
      drawers.forEach((observers, drawer) => {
        if (event.target.contains(drawer)) {
          observers.state.disconnect();
          observers.content.disconnect();
          drawers.delete(drawer);
        }
      });
    }, { signal: lifecycle.signal });
    refresh();
  }

  function stop() {
    lifecycle?.abort();
    lifecycle = null;
    drawers.forEach(({ state, content }) => { state.disconnect(); content.disconnect(); });
    drawers.clear();
  }

  window.shopdePayPalMessages = { refresh };
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', start);
  start();
})();
